import { createContext, ReactNode, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetMessageThreadsQuery } from "src/api/services/messagesApi";
import { MatchParam } from "src/constants/matches";

type Thread = {
  id: string;
  name: string | null;
  message: string | null;
  unreadTotal: number;
  is_active: boolean;
  product_category: {
    name: string;
  };
  product: {
    name: string;
    slug: string;
  };
};

interface ContextType {
  threads: Thread[];
  currentThread: Thread | undefined;
}

export const MessageThreadsContext = createContext<ContextType>({
  threads: [],
  currentThread: undefined,
});

interface Props {
  children: ReactNode;
}

const MessageThreadsProvider = ({ children }: Props) => {
  const { data } = useGetMessageThreadsQuery();
  const { threadId } = useParams<MatchParam<"THREAD_ID">>();

  const threads = useMemo(() => {
    if (data == null) return [];

    return data.map((thread) => {
      const name = "";
      return {
        product: thread.product,
        id: thread.slug,
        name: name,
        message: thread.last_message,
        product_category: thread.product_category,
        unreadTotal: 0,
        is_active: thread.is_active,
      };
    });
  }, [data]);

  const currentThread = useMemo(() => {
    return threads.find((thread) => thread.id === threadId);
  }, [threadId, threads]);

  const value = useMemo(
    () => ({
      threads,
      currentThread,
    }),
    [currentThread, threads],
  );

  return (
    <MessageThreadsContext.Provider value={value}>
      {children}
    </MessageThreadsContext.Provider>
  );
};

export default MessageThreadsProvider;
